import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Crown as _CrownUnused } from "lucide-react";
import CommunityIQLogo from "@/components/CommunityIQLogo";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", unit_number: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      nav("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background paper-texture flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center justify-center mb-4" data-testid="register-brand-link">
          <CommunityIQLogo className="w-48 h-48 -my-8" />
        </Link>

        <div className="hoa-card p-8 royal-ring">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-1">Create your account</h1>
          <p className="text-sm text-muted-foreground mb-6">New homeowners sign up here.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" required value={form.name} onChange={set("name")} className="mt-1.5 h-11" data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 h-11" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-1.5 h-11" data-testid="register-password-input" />
              <p className="text-xs text-muted-foreground mt-1">At least 6 characters.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="unit">Unit number</Label>
                <Input id="unit" value={form.unit_number} onChange={set("unit_number")} className="mt-1.5 h-11" placeholder="A-101" data-testid="register-unit-input" />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" value={form.phone} onChange={set("phone")} className="mt-1.5 h-11" placeholder="Optional" data-testid="register-phone-input" />
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive" data-testid="register-error">{error}</p>
            )}
            <Button type="submit" className="w-full h-11 rounded-full" disabled={loading} data-testid="register-submit-btn">
              {loading ? "Creating..." : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-center text-muted-foreground mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-gold font-medium hover:underline" data-testid="go-to-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
